import { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { MessageCircle, ArrowLeft } from "lucide-react";
import { WA_BOX_CLASSICA, WA_BOX_LUXURY } from "./Navbar";

const BOXES = [
  {
    id: "classica",
    name: "Box Apericena",
    subtitle: "La semplicità fatta bene",
    desc: "20 stuzzichini salati artigianali, preparati da noi dall'impasto al ripieno. Una selezione di gusti semplici e sfiziosi, perfetta per organizzare un apericena a casa senza dover pensare a nulla.",
    meta: "20 stuzzichini · 40 €",
    wa: WA_BOX_CLASSICA,
  },
  {
    id: "luxury",
    name: "Box Apericena Luxury",
    subtitle: "Un apericena con qualcosa in più",
    desc: "20 stuzzichini salati artigianali con farciture più particolari e ricercate, pensati per chi vuole portare in tavola un apericena diverso dal solito, già pronto da condividere.",
    meta: "20 stuzzichini · 50 €",
    wa: WA_BOX_LUXURY,
    highlight: "La più ricercata",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

const BoxArt = ({ luxury = false }) => (
  <svg viewBox="0 0 320 240" className="w-4/5 max-w-[300px]" role="img" aria-hidden="true">
    <rect x="14" y="12" width="292" height="216" rx="16" fill="none" stroke="#84232a" strokeOpacity="0.25" strokeWidth="1.5" strokeDasharray="2 8" />
    <g fill="none" stroke="#84232a" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      <path d="M95 52 L225 52 L252 104 L68 104 Z" />
      <path d="M106 64 L214 64" strokeWidth="2" strokeDasharray="1 8" />
      <circle cx="102" cy="118" r="11" fill="#84232a" fillOpacity="0.06" />
      <circle cx="138" cy="115" r="12" fill="#84232a" fillOpacity="0.06" />
      <circle cx="182" cy="115" r="12" fill="#84232a" fillOpacity="0.06" />
      <circle cx="218" cy="118" r="11" fill="#84232a" fillOpacity="0.06" />
      <path d="M98 114 C100 110 104 110 106 114" strokeWidth="2" />
      <path d="M134 110 C136 106 140 106 142 110" strokeWidth="2" />
      <path d="M178 110 C180 106 184 106 186 110" strokeWidth="2" />
      <path d="M214 114 C216 110 220 110 222 114" strokeWidth="2" />
      <path d="M70 104 L250 104 L250 198 L70 198 Z" />
      <path d="M70 128 L250 128" strokeWidth="2" />
      <path d="M148 158 L152 180 L168 180 L172 158" strokeWidth="2.4" />
      <path d="M144 158 C140 150 148 146 152 149 C154 141 166 141 168 149 C174 146 178 152 176 158 Z" strokeWidth="2.4" />
    </g>
    <circle cx="160" cy="145" r="2.6" fill="#84232a" />
    {luxury && (
      <g fill="none" stroke="#84232a" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
        <path d="M160 52 C142 36 126 50 146 58 C152 60 157 56 160 52" />
        <path d="M160 52 C178 36 194 50 174 58 C168 60 163 56 160 52" />
        <circle cx="160" cy="53" r="4.5" fill="#84232a" fillOpacity="0.12" />
        <path d="M56 40 L58.5 47 L66 49.5 L58.5 52 L56 59 L53.5 52 L46 49.5 L53.5 47 Z" fill="#84232a" fillOpacity="0.15" strokeWidth="2" />
        <path d="M272 74 L274 79 L279 81 L274 83 L272 88 L270 83 L265 81 L270 79 Z" fill="#84232a" fillOpacity="0.15" strokeWidth="2" />
      </g>
    )}
  </svg>
);

const BoxCard = ({ box, i }) => (
  <motion.article
    variants={fadeUp}
    custom={i}
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, margin: "-60px" }}
    className="relative bg-[#fffdfa] border border-[#e6dacb] rounded-3xl overflow-hidden flex flex-col"
    data-testid={`box-card-${box.id}`}
  >
    {box.highlight && (
      <span className="absolute top-4 right-4 z-10 text-[0.65rem] uppercase tracking-[0.2em] font-semibold bg-[#84232a] text-[#fbf6f1] px-3 py-1.5 rounded-full">
        {box.highlight}
      </span>
    )}
    <div
      className="w-full aspect-[4/3] bg-[#f4ede4] border-b border-[#e6dacb] flex items-center justify-center"
      data-testid={`box-art-${box.id}`}
    >
      <BoxArt luxury={box.id === "luxury"} />
    </div>
    <div className="p-7 sm:p-9 flex flex-col grow">
      <div className="flex items-start justify-between gap-4">
        <h2 className="font-display text-3xl sm:text-4xl font-semibold text-black leading-tight">
          {box.name}
        </h2>
      </div>
      <p className="font-script text-2xl text-[#84232a] mt-2">{box.subtitle}</p>
      <p className="text-[#4a423d] text-sm sm:text-base leading-relaxed mt-4 grow">{box.desc}</p>
      <p className="text-[0.7rem] uppercase tracking-[0.25em] font-semibold text-[#84232a] mt-5">
        {box.meta}
      </p>
      <a
        href={box.wa}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`box-cta-whatsapp-${box.id}`}
        className="group mt-6 inline-flex items-center justify-center gap-3 bg-[#84232a] text-[#fbf6f1] px-7 py-3.5 rounded-full text-sm font-medium hover:bg-[#63191f] transition-colors duration-300"
      >
        <MessageCircle className="w-4 h-4 transition-transform duration-300 group-hover:rotate-12" />
        Ordina su WhatsApp
      </a>
    </div>
  </motion.article>
);

export default function BoxApericena() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main className="pt-[76px]" data-testid="box-page">
      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Link
            to="/"
            data-testid="back-to-home-link"
            className="inline-flex items-center gap-2 text-sm font-medium text-black/60 hover:text-[#84232a] transition-colors duration-300 mb-10"
          >
            <ArrowLeft className="w-4 h-4" />
            Torna al sito
          </Link>

          <motion.p
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            className="flex items-center gap-4 text-[0.7rem] uppercase tracking-[0.35em] font-medium text-[#84232a] mb-6"
            data-testid="box-eyebrow"
          >
            <span className="w-10 h-px bg-[#84232a]/50" />
            Le Box Apericena
          </motion.p>
          <motion.h1
            variants={fadeUp}
            custom={1}
            initial="hidden"
            animate="visible"
            className="font-display text-5xl sm:text-6xl font-medium text-black leading-[1.02] max-w-2xl"
            data-testid="box-title"
          >
            Due box, una sola idea:{" "}
            <span className="font-script text-[#84232a] font-normal">l'apericena perfetta.</span>
          </motion.h1>
          <motion.p
            variants={fadeUp}
            custom={2}
            initial="hidden"
            animate="visible"
            className="text-[#4a423d] text-base sm:text-lg leading-relaxed mt-6 max-w-xl font-light"
            data-testid="box-intro"
          >
            Stuzzichini salati preparati interamente da noi, dall'impasto alla
            farcitura, racchiusi in una box pronta da portare in tavola. Tu scegli
            la versione, al resto pensiamo noi.
          </motion.p>

          <div className="grid md:grid-cols-2 gap-6 sm:gap-8 mt-14 max-w-5xl">
            {BOXES.map((box, i) => (
              <BoxCard key={box.id} box={box} i={i} />
            ))}
          </div>

          <motion.p
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="text-xs text-[#4a423d] tracking-wide mt-12 pt-6 border-t border-[#e6dacb] max-w-5xl"
            data-testid="box-pickup-note"
          >
            Ritiro in pasticceria in Via S. Vigilio 8, Trento · Martedì – Domenica 8:00 – 19:30 · Lunedì chiuso
          </motion.p>
        </div>
      </section>
    </main>
  );
}
